import React from "react";
import { aiClearHistory } from "../../../../api/api";
import { useAiChat } from "../../../../hooks/useAiChat";
import { clearSkillMetrics, endRun as endMetricsRun, mark, startRun, timer } from "../../../../state/ai/runMetrics";
import { getToolUseLabel } from "../../../../hooks/useAiTools";
import useApi from "../../../../hooks/useApi";
import { useSkills } from "../../../../state/api/ai/useSkills";
import { tryGetWorkbookContainerBlock } from "../../../../util/officeFile";
import Button from "../../buttons/Button";
import Icon from "../../icons/Icon";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader
} from "../../modal/ModalForm";
import Pill from "../../pill/Pill";

interface Activity {
  label: string;
  count: number;
}

const fmtTime = (secs: number) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m ? `${m}:${s.toString().padStart(2, "0")}` : `${s}s`;
};


function collectActivity(chatHistory: any[] | undefined): { steps: Activity[]; writing: boolean } {
  const groups = new Map<string, Activity>();
  let writing = false;

  const bump = (key: string, label: string) => {
    const existing = groups.get(key);
    if (existing) existing.count += 1;
    else groups.set(key, { label, count: 1 });
  };

  for (const m of chatHistory ?? []) {
    for (const b of m.content ?? []) {
      if (!b || typeof b.type !== "string") continue;
      const t = b.type;

      if (t === "text") {
        writing = m.role === "assistant";
        continue;
      }
      if (t === "tool_result") continue;

      writing = false;

      const u = getToolUseLabel(t, b);
      if (!u) continue;
      bump(u.label, u.label);
    }
  }

  return { steps: Array.from(groups.values()), writing };
}

export default function RunAnalysisButton({ skill }: { skill: string }) {

  const { addMessage, chatHistory, mutate, thinkingSince } = useAiChat({ skill });
  const { skillsById } = useSkills();

  const skillMeta = skillsById?.[skill];

  /**
   * One clock, held from the click until addMessage returns. Handing over to
   * thinkingSince instead leaves a frame where neither is set, and this React
   * version renders that frame: the dialog unmounts, losing the visible state
   * it keeps internally, and comes back as a new one.
   */
  const [runningSince, setRunningSince] = React.useState<number | null>(null);
  const [preparing, setPreparing] = React.useState<boolean>(false);

  const callApi = useApi();

  const { steps } = React.useMemo(() => collectActivity(chatHistory), [chatHistory]);

  const [now, setNow] = React.useState<number>(() => Date.now());

  React.useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const busySince = runningSince ?? thinkingSince;
  const elapsed = busySince ? Math.floor((now - busySince) / 1000) : 0;
  const hasAnalysis = !!chatHistory?.length;

  if (busySince) {
    return (
      <Dialog
        key={"run-dialog"}
        visible
        trigger={
          <Button loading />
        }
        dialog={
          <DialogContent>
            <DialogHeader
              title="Grab a coffee! ☕"
              description={`Analyzing your deal — ${fmtTime(elapsed)}`}
            />
            <DialogBody>
              {steps.length === 0 && (
                <div className="d-flex flex-row align-items-center mb-2 gap-2">
                  <Icon loading icon="clock" />
                  <span>{preparing ? "Reading your workbook" : "Starting analysis"}...</span>
                </div>
              )}
              {steps.map((s, i) => {
                const isLast = i === steps.length - 1;
                const active = isLast;
                const completed = !isLast;
                return (
                  <div key={`${s.label}-${i}`} className="d-flex flex-row align-items-center mb-2 gap-2">
                    <Icon loading={active} icon={completed ? "check" : "clock"} color={completed ? "green" : undefined} />
                    <span>{s.label}...</span>
                    {s.count > 1 && <Pill>{s.count}x</Pill>}
                  </div>
                );
              })}
            </DialogBody>
          </DialogContent>
        }
      />
    )
  }

  if (hasAnalysis) return (
    <Dialog
      key={"close-dialog"}
      trigger={(
        <Button
          icon="arrow-clockwise"
          color="var(--bs-danger)"
          text="Exit Analysis"
        />
      )}
      dialog={
        close => (
          <DialogContent>
            <DialogHeader
              title="Warning"
              description="Do you really want to continue?"
            />
            <DialogBody>
              <span>This will remove the current deal analysis.</span>
              <span>Please confirm your action.</span>
            </DialogBody>
            <DialogFooter>
              <Button
                text="Confirm"
                color="var(--bs-warning)"
                icon="check"
                onClick={async () => {
                  clearSkillMetrics(skill);

                  await callApi(aiClearHistory({
                    skill
                  }));

                  await mutate(_ => [], { revalidate: false });
                  close();
                }}
              />
            </DialogFooter>
          </DialogContent>
        )}
    />
  )


  return (
    <Button
      onClick={async () => {
        setRunningSince(Date.now());

        clearSkillMetrics(skill);
        startRun(skill);
        mark(skill, "analysis:click");

        try {
          await mutate(() => [], { revalidate: false });

          // Reading the workbook does not depend on the history being cleared, and
          // both cost seconds, so the run waits for the slower one instead of the sum.
          const uploads = !!skillMeta?.modelFileUploadOnStart;

          setPreparing(uploads);

          let wb: Record<string, any> | null = null;

          const prepDone = timer(skill, "analysis:prepare");

          try {
            [wb] = await Promise.all([
              uploads ? tryGetWorkbookContainerBlock(skill) : Promise.resolve(null),
              (async () => {
                const clearDone = timer(skill, "history:clear");
                const res = await callApi(aiClearHistory({ skill }));
                clearDone({ ok: res?.success !== false });
                return res;
              })()
            ]);
          }
          finally {
            prepDone({ attached: !!wb });
            setPreparing(false);
          }

          await addMessage(wb ? [wb] : undefined);
        }
        finally {
          mark(skill, "analysis:done");
          endMetricsRun(skill);
          setRunningSince(null);
        }
      }}
      icon="play"
      text="Analyze Deal"
    />
  )
}